import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border-0 border-b border-white/20 px-0 py-3 text-white placeholder:text-white/30 focus:border-gold focus:ring-0 focus:outline-none transition-colors duration-300";
const labelCls = "block text-[11px] font-mono uppercase tracking-[0.2em] text-white/40 mb-1";

const detailsToText = (details) => (details || []).map((d) => `${d.label}: ${d.value}`).join("\n");
const textToDetails = (text) =>
  text.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const i = l.indexOf(":");
    return i > 0 ? { label: l.slice(0, i).trim(), value: l.slice(i + 1).trim() } : { label: "Info", value: l };
  });

export const AdminPrestations = () => {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState("");
  const [form, setForm] = useState({ title: "", accroche: "", description: "", details: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/prestations").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const pick = (p) => {
    setSel(p.slug);
    setForm({ title: p.title, accroche: p.accroche || "", description: p.description || "", cta: p.cta || "", details: detailsToText(p.details) });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/prestations/${sel}`, {
        title: form.title,
        accroche: form.accroche,
        description: form.description,
        cta: form.cta,
        details: textToDetails(form.details),
      });
      toast.success("Fiche mise à jour.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4 space-y-2" data-testid="admin-prestations-list">
        {list.map((p) => (
          <button key={p.slug} onClick={() => pick(p)} data-testid={`admin-prestation-${p.slug}`}
            className={`w-full text-left px-5 py-4 border transition-colors duration-300 ${
              sel === p.slug ? "border-gold bg-gold/10 text-white" : "border-white/10 text-white/60 hover:border-gold/50"
            }`}>
            <span className="font-mono text-xs text-gold/70 mr-3">/{String(p.order).padStart(2, "0")}</span>
            <span className="font-serif text-lg">{p.title}</span>
            <span className="block text-[10px] font-mono uppercase tracking-[0.2em] text-white/30 mt-1">{p.pole}</span>
          </button>
        ))}
      </div>
      <div className="lg:col-span-8">
        {!sel ? (
          <p className="text-white/40 py-10" data-testid="admin-prestations-empty">Sélectionnez une fiche à modifier.</p>
        ) : (
          <form onSubmit={save} className="space-y-8" data-testid="admin-prestation-form">
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label htmlFor="pf-title" className={labelCls}>Titre</label>
                <input id="pf-title" data-testid="prestation-title-input" required value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label htmlFor="pf-accroche" className={labelCls}>Accroche</label>
                <input id="pf-accroche" data-testid="prestation-accroche-input" value={form.accroche}
                  onChange={(e) => setForm({ ...form, accroche: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div>
              <label htmlFor="pf-cta" className={labelCls}>Texte du bouton devis (CTA)</label>
              <input id="pf-cta" data-testid="prestation-cta-input" value={form.cta || ""}
                onChange={(e) => setForm({ ...form, cta: e.target.value })}
                placeholder="Ex : Demander un devis pour une customisation" className={inputCls} />
            </div>
            <div>
              <label htmlFor="pf-desc" className={labelCls}>Description</label>
              <textarea id="pf-desc" data-testid="prestation-description-input" rows={5} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} resize-none`} />
            </div>
            <div>
              <label htmlFor="pf-details" className={labelCls}>Détails — une ligne par champ, format « Label: valeur »</label>
              <textarea id="pf-details" data-testid="prestation-details-input" rows={6} value={form.details}
                onChange={(e) => setForm({ ...form, details: e.target.value })}
                placeholder={"Supports: Sneakers, sacs\nPour: Marques, particuliers"}
                className={`${inputCls} resize-none font-mono text-sm`} />
            </div>
            <button type="submit" data-testid="prestation-save-btn" disabled={saving}
              className="bg-gold text-black px-10 py-4 text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-300 hover:bg-gold-light disabled:opacity-50">
              {saving ? "Enregistrement…" : "Enregistrer la fiche"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
